import { LinkedNavigationItem } from '@pwc/pwc-deals-ui';
import {
  CommentTarget,
  NavigationNodeId,
  USER_ROLES,
  Constants,
} from '@pwc/pwc-deals-web-common';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import _orderBy from 'lodash/orderBy';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { Link } from 'react-router-dom';

import { getProject } from 'features/project/projectSlice';
import { wrapWithCommentingWrapper } from 'features/project/ViewProject/ProjectOverview/utils/wrapWithCommentingWrapper';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import {
  getViewPathForAnalysis,
  getViewPathForKfa,
} from '../../../features/analyses/common/analysisUrlHelper';
import {
  getAugmentedAnalyses,
  sortAnalysesByDate,
} from '../../helpers/analysesUtils';
import useAllConnections from '../../../features/narratives/hooks/useAllConnections';
import { contentIsLinked } from '../../../features/narratives/narrativesUtilities';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import LinkedConnectionsAccordion from './LinkedConnectionsAccordion';
import UnableToLoadContent from '../../../features/analyses/common/UnableToLoadContent/UnableToLoadContent';

import './LinkedContentNavigation.scss';
import styles from './LinkedContentNavigation.module.scss';
import { useKFATitleI18n } from '../../hooks/useKFATitleI18n';

export const LinkedContentNavigation = ({
  hypothesisIndex,
  projectId,
  isKfa = false,
  connectedAnalyses = [],
  connectedKfas = [],
  currentContent = {},
  sortByDateModified = true,
  parentAllRetrievedConnections = null,
  parentError = null,
  useParentConnections = false,
  isCommentingAllowedOnThisItem,
  disableAllLinks = false,
}) => {
  // we need to filter out inprogress KFAs and analysis if current content is in client draft status
  const filteredKfas = useMemo(() => {
    return currentContent.versionStatus ===
      Constants.VERSION_STATUS.SHARED.value
      ? connectedKfas.filter(
          (kfa) => kfa.versionStatus !== Constants.VERSION_STATUS.DRAFT.value
        )
      : connectedKfas;
  }, [connectedKfas, currentContent.versionStatus]);

  const filteredAnalyses = useMemo(() => {
    return currentContent.versionStatus ===
      Constants.VERSION_STATUS.SHARED.value
      ? connectedAnalyses.filter(
          (analysis) =>
            analysis.versionStatus !== Constants.VERSION_STATUS.DRAFT.value
        )
      : connectedAnalyses;
  }, [connectedAnalyses, currentContent.versionStatus]);

  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { t } = useTranslation();

  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);
  const KFATitleI18n = useKFATitleI18n(KFATitle);

  const { navigation = [] } = useSelector(getProject);
  const isKfaHidden = useMemo(
    () =>
      navigation.find((nav) => nav.id === NavigationNodeId.KEY_FOCUS_AREAS)
        ?.isHidden,
    [navigation]
  );
  const sortedAnalyses = useMemo(
    () => _orderBy(filteredAnalyses, ['dateModified'], ['desc']),
    [filteredAnalyses]
  );

  const analysesTitle = currentContent.title;
  const isMountedRef = useIsMountedRef();

  const [isAccordionClosed, setIsAccordionClosed] = useState(false);
  const [remainedContent, setRemainedContent] = useState([]);
  const [allConnections, setAllConnections] = useState(null);
  const [visibleKFAsAnalyses, setVisibleKFAsAnalyses] = useState([]);

  const { allConnections: allRetrievedConnections, error } =
    !useParentConnections
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useAllConnections(isMountedRef.current)
      : { allConnections: parentAllRetrievedConnections, error: parentError };

  const limit = 4;

  useEffect(() => {
    if (!allConnections && allRetrievedConnections) {
      setAllConnections(allRetrievedConnections);
    }
  }, [allRetrievedConnections, allConnections, setAllConnections]);

  const filteredAnalysesForKfa = useMemo(() => {
    const filtered = [];
    if (!isKfa && allConnections && filteredKfas.length) {
      allConnections.analyses.filter((content) => {
        if (contentIsLinked(content.links, filteredKfas[0].contentId)) {
          filtered.push(content);
        }
        return [];
      });

      return !isKfa ? filtered : sortedAnalyses;
    }
    return !isKfa ? filtered : sortedAnalyses;
  }, [allConnections, isKfa, filteredKfas, sortedAnalyses]);

  const markAsGrey = (connectionContent) =>
    connectionContent &&
    connectionContent.contentThreadId === currentContent.contentThreadId;

  useEffect(() => {
    if (
      filteredAnalysesForKfa.length &&
      allConnections &&
      allConnections.analyses &&
      allConnections.analyses.length
    ) {
      const visibleContent = filteredAnalysesForKfa.slice(0, limit);
      if (sortByDateModified) {
        setVisibleKFAsAnalyses(
          sortAnalysesByDate(visibleContent, allConnections)
        );
      } else {
        setVisibleKFAsAnalyses(
          getAugmentedAnalyses(visibleContent, allConnections)
        );
      }
    }
  }, [
    filteredAnalysesForKfa,
    allConnections,
    sortedAnalyses,
    sortByDateModified,
  ]);

  const onAccordionOpen = useCallback(() => {
    const visibleContent = filteredAnalysesForKfa.slice(
      limit,
      filteredAnalysesForKfa.length
    );
    setRemainedContent(visibleContent);
  }, [filteredAnalysesForKfa, setRemainedContent]);

  const isRelatedAnalyses = !isKfa || sortedAnalyses.length;

  const emptyMessages = () => {
    if (isKfa) {
      return t('noAnalysesConnected');
    }
    return t('noRelatedAnalysesHaveBeenConnectedToThisAnalysis');
  };

  if (error) {
    return (
      <div
        className="connected-analyses"
        data-testid="analyses-and-insights-details"
      >
        <div>
          <span
            data-testid="connected-analyses-header"
            className={classNames('connected-analyses__header')}
          >
            {t('connections')}
          </span>
        </div>
        <div
          className="connected-navigation-empty connected-navigation-empty__error"
          data-testid={`hypotheses-section-error-${hypothesisIndex}`}
        >
          <UnableToLoadContent projectId={projectId} />
        </div>
      </div>
    );
  }

  const onGetViewPathForAnalysis = (projectIdArg, analysis) => {
    return getViewPathForAnalysis(projectIdArg, analysis, isKfa, isPwcUser);
  };

  return (
    <div
      className={classNames('connected-analyses', {
        [styles['disable-links']]: disableAllLinks,
      })}
      data-testid="analyses-and-insights-details"
    >
      <div>
        {(!isKfaHidden && filteredKfas.length) || isKfa ? (
          <div
            className={classNames('', {
              'connected-analyses__has_analyses':
                filteredKfas.length || filteredAnalysesForKfa.length,
            })}
          >
            <LinkedNavigationItem
              title={KFATitle}
              projectId={+projectId}
              items={filteredKfas}
              hypothesisIndex={hypothesisIndex}
              getViewPathForAnalysis={getViewPathForKfa}
              key={hypothesisIndex}
              analysesTitle=""
              wrapWithCommentingWrapper={(component) => {
                return wrapWithCommentingWrapper(
                  component,
                  CommentTarget.ConnectedKfa,
                  isCommentingAllowedOnThisItem
                );
              }}
            />

            {filteredAnalysesForKfa.length ? (
              <div>
                {wrapWithCommentingWrapper(
                  <span
                    data-testid="connected-analyses-header"
                    className={classNames('connected-analyses__header')}
                  >
                    {t('analyses')}
                  </span>,
                  CommentTarget.ConnectedAnalyses,
                  isCommentingAllowedOnThisItem
                )}

                {visibleKFAsAnalyses.map((analysis, index) => (
                  <div
                    className="connected-analyses-item"
                    key={`${analysis.title}-${analysis.contentId}-${analysis.contentThreadId}`}
                  >
                    {markAsGrey(analysis)
                      ? wrapWithCommentingWrapper(
                          <div className="connected-analyses__first">
                            {analysis.title}
                          </div>,
                          CommentTarget.ConnectedAnalyses,
                          isCommentingAllowedOnThisItem
                        )
                      : wrapWithCommentingWrapper(
                          <Link
                            to={getViewPathForAnalysis(
                              projectId,
                              analysis,
                              isKfa,
                              isPwcUser
                            )}
                            key={analysis.contentId}
                            data-testid={`navigation-connected-content-${hypothesisIndex}-${index}`}
                            className="connected-analyses__list__link"
                          >
                            {analysis.title}
                          </Link>,
                          CommentTarget.ConnectedAnalyses,
                          isCommentingAllowedOnThisItem
                        )}
                  </div>
                ))}
              </div>
            ) : null}

            {filteredAnalysesForKfa.length > limit && (
              <div className="connected-analyses__show_more__accordion">
                <LinkedConnectionsAccordion
                  allConnections={remainedContent}
                  currentContent={currentContent}
                  projectId={projectId}
                  title={
                    isAccordionClosed
                      ? t('showLessKFA')
                      : `${t('show')} ${Number(
                          filteredAnalysesForKfa.length - limit
                        )} ${t('more')} `
                  }
                  width={18}
                  onExpand={onAccordionOpen}
                  onClose={() => {
                    setIsAccordionClosed(!isAccordionClosed);
                  }}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>
      {isKfa &&
        !sortedAnalyses.length &&
        wrapWithCommentingWrapper(
          <div
            className="connected-navigation-empty"
            data-testid={`hypotheses-section-empty-${hypothesisIndex}`}
          >
            {t('noAnalysesConnected')}
          </div>,
          CommentTarget.ConnectedAnalyses,
          isCommentingAllowedOnThisItem,
          'connected-navigation-empty-container'
        )}
      {!isKfaHidden &&
        !filteredKfas.length &&
        !isKfa &&
        wrapWithCommentingWrapper(
          <div
            className="connected-navigation-empty"
            data-testid={`hypotheses-section-empty-${hypothesisIndex}`}
          >
            {t('noKeyFocusAreaConnected', { KFATitle: KFATitleI18n })}
          </div>,
          CommentTarget.ConnectedKfa,
          isCommentingAllowedOnThisItem,
          'connected-navigation-empty-container'
        )}
      {!sortedAnalyses.length && !isKfa
        ? wrapWithCommentingWrapper(
            <div
              className="connected-navigation-empty"
              data-testid={`analyses-section-empty-${hypothesisIndex}`}
            >
              {emptyMessages()}
            </div>,
            CommentTarget.ConnectedRelatedAnalyses,
            isCommentingAllowedOnThisItem,
            'connected-navigation-empty-container'
          )
        : null}
      <div>
        {sortedAnalyses.length && !isKfa ? (
          <div
            data-testid="related-analyses-container"
            className="connected-analyses__has_analyses_dropdown"
          >
            <LinkedNavigationItem
              title={t('relatedAnalyses')}
              analysesTitle={analysesTitle}
              projectId={+projectId}
              items={sortedAnalyses}
              hypothesisIndex={hypothesisIndex}
              isRelatedAnalyses={isRelatedAnalyses}
              getViewPathForAnalysis={onGetViewPathForAnalysis}
              wrapWithCommentingWrapper={(component) => {
                return wrapWithCommentingWrapper(
                  component,
                  CommentTarget.ConnectedRelatedAnalyses,
                  isCommentingAllowedOnThisItem
                );
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

LinkedContentNavigation.propTypes = {
  hypothesisIndex: PropTypes.number.isRequired,
  projectId: PropTypes.string.isRequired,
};
